
import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, writeBatch } from "firebase/firestore";
import { DecimalEnum } from "@/types/fieldsOptions";
import { Firestore } from "firebase/firestore";

export const getDecimalEnums = async (db: Firestore, type?: string): Promise<DecimalEnum[]> => {
  try {
    const decimalEnumsRef = collection(db, "decimalEnums");
    const snapshot = await getDocs(decimalEnumsRef);
    
    const allEnums = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as DecimalEnum));
    
    // Filter by type if specified
    if (type) {
      return allEnums.filter(item => item.type === type);
    }
    
    return allEnums;
  } catch (error) {
    console.error("Error fetching decimal enums:", error);
    throw error;
  }
};

export const getDecimalEnumById = async (db: Firestore, id: string): Promise<DecimalEnum | null> => {
  try {
    const docRef = doc(db, "decimalEnums", id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as DecimalEnum;
    }
    
    return null;
  } catch (error) {
    console.error(`Error fetching decimal enum with ID ${id}:`, error);
    throw error;
  }
};

export const createDecimalEnum = async (db: Firestore, data: Omit<DecimalEnum, "id">, customId?: string): Promise<DecimalEnum> => {
  try {
    let docRef;
    if (customId) {
      docRef = doc(db, "decimalEnums", customId);
    } else {
      docRef = doc(collection(db, "decimalEnums"));
    }
    
    await setDoc(docRef, data);
    
    return {
      id: docRef.id,
      ...data
    };
  } catch (error) {
    console.error("Error creating decimal enum:", error);
    throw error;
  }
};

export const updateDecimalEnum = async (db: Firestore, id: string, data: Partial<Omit<DecimalEnum, "id">>): Promise<void> => {
  try {
    const docRef = doc(db, "decimalEnums", id);
    await setDoc(docRef, data, { merge: true });
  } catch (error) {
    console.error(`Error updating decimal enum with ID ${id}:`, error);
    throw error;
  }
};

export const deleteDecimalEnum = async (db: Firestore, id: string): Promise<void> => {
  try {
    const docRef = doc(db, "decimalEnums", id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(`Error deleting decimal enum with ID ${id}:`, error);
    throw error;
  }
};

export const deleteDecimalEnumsBulk = async (db: Firestore, ids: string[]): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    ids.forEach(id => {
      const docRef = doc(db, "decimalEnums", id);
      batch.delete(docRef);
    });
    
    await batch.commit();
  } catch (error) {
    console.error(`Error bulk deleting decimal enums:`, error);
    throw error;
  }
};

export const initializeDecimalEnums = async (db: Firestore): Promise<void> => {
  try {
    const snapshot = await getDocs(collection(db, "decimalEnums"));
    
    if (!snapshot.empty) {
      console.log("Decimal enums already exist. Skipping initialization.");
      return;
    }
    
    const initialData = [
      ["g_prek", -1, "grade", "Pre-K", "Pre-K"],
      ["g_rk", -0.5, "grade", "Rising K", "Rising Kindergarten"],
      ["g_k", 0, "grade", "K", "Kindergarten"],
      ["g_r1", 0.5, "grade", "Rising 1st", "Rising 1st grade"],
      ["g_1", 1, "grade", "1st", "1st grade"],
      ["g_r2", 1.5, "grade", "Rising 2st", "Rising 2nd grade"],
      ["g_2", 2, "grade", "2nd", "2nd grade"],
      ["g_r3", 2.5, "grade", "Rising 3rd", "Rising 3rd grade"],
      ["g_3", 3, "grade", "3rd", "3rd grade"],
      ["g_r4", 3.5, "grade", "Rising 4th", "Rising 4th grade"],
      ["g_4", 4, "grade", "4th", "4th grade"],
      ["g_r5", 4.5, "grade", "Rising 5th", "Rising 5th grade"],
      ["g_5", 5, "grade", "5th", "5th grade"],
      ["g_r6", 5.5, "grade", "Rising 6th", "Rising 6th grade"],
      ["g_6", 6, "grade", "6th", "6th grade"],
      ["g_r7", 6.5, "grade", "7th", "7th grade"],
      ["g_7", 7, "grade", "7th", "7th grade"],
      ["g_r8", 7.5, "grade", "Rising 8th", "Rising 8th grade"],
      ["g_8", 8, "grade", "8th", "8th grade"],
      ["g_r9", 8.5, "grade", "Rising 9th", "Rising 9th grade"],
      ["g_9", 9, "grade", "9th", "9th grade"],
      ["g_r10", 9.5, "grade", "Rising 10th", "Rising 10th grade"],
      ["g_10", 10, "grade", "10th", "10th grade"],
      ["g_r11", 10.5, "grade", "Rising 11th", "Rising 11th grade"],
      ["g_11", 11, "grade", "11th", "11th grade"],
      ["g_r12", 11.5, "grade", "Rising 12th", "Rising 12th grade"],
      ["g_12", 12, "grade", "12th", "12th grade"]
    ];
    
    const batch = writeBatch(db);
    
    initialData.forEach(([docId, value, type, shortLabel, longLabel]) => {
      const docRef = doc(db, "decimalEnums", String(docId));
      
      batch.set(docRef, {
        value: Number(value),
        type,
        shortLabel, // Short label
        longLabel,  // Long label for display in UI
        altValue: null // Include altValue with default null
      });
    });
    
    await batch.commit();
    console.log("Decimal enums initialized successfully.");
  } catch (error) {
    console.error("Error initializing decimal enums:", error);
    throw error;
  }
};
